/**
 * Meta (Facebook/Instagram) Pixel — client-side helpers.
 *
 * Companion to the <MetaPixel> component that injects the base script. This
 * module exposes the small set of operations the rest of the app needs:
 *
 *   - trackEvent(eventName, params?, eventId?)  → fbq('track', ...)
 *   - generateEventId(uid, eventName)           → deterministic id for browser/CAPI dedup
 *   - getPixelCookies()                         → reads _fbp / _fbc from document.cookie
 *   - captureFbclidIfMissing()                  → sets _fbc on .gobo.show from a fresh fbclid
 *
 * Everything is a safe no-op when NEXT_PUBLIC_META_PIXEL_ID is unset or fbq
 * hasn't loaded yet, so signup and onboarding can never be blocked by a
 * missing Pixel.
 */

import Bugsnag from './bugsnag';
import { metaPixelId } from './metaPixelId';

// Window typing for the FB Pixel global. The Pixel snippet sets up `fbq` as a
// function with a `q` queue while the SDK is still loading; we only declare
// what we read.
declare global {
  interface Window {
    fbq?: ((...args: unknown[]) => void) & {
      queue?: unknown[];
      loaded?: boolean;
      version?: string;
    };
    _fbq?: Window['fbq'];
  }
}

const PIXEL_ID = metaPixelId();

/** Returns true if we have a Pixel ID configured AND we're in the browser. */
export function isPixelEnabled(): boolean {
  return typeof window !== 'undefined' && !!PIXEL_ID;
}

/**
 * Fire a Pixel event. Silently no-ops when the Pixel isn't configured or the
 * SDK hasn't booted yet — by design, this must never block signup.
 *
 * `eventId` is passed as `eventID` (Pixel's casing) so it matches the
 * `event_id` we send to the Conversions API; Meta uses it to deduplicate
 * the browser + server fires of the same conversion.
 */
export function trackEvent(
  eventName: string,
  params?: Record<string, unknown>,
  eventId?: string
): void {
  if (!isPixelEnabled()) return;
  try {
    const fbq = window.fbq;
    if (typeof fbq !== 'function') return;
    if (eventId) {
      fbq('track', eventName, params || {}, { eventID: eventId });
    } else {
      fbq('track', eventName, params || {});
    }
  } catch (err) {
    Bugsnag.notify(err instanceof Error ? err : new Error(String(err)), (event) => {
      event.addMetadata('meta_pixel', { eventName, hadEventId: !!eventId });
    });
  }
}

/**
 * Deterministic event id derived from the user's uid + the conversion event
 * name. Using a deterministic id (instead of a random UUID) eliminates the
 * cross-tab / cross-refresh race where two simultaneous fires would survive
 * the Firestore idempotency check and produce duplicate events in Meta.
 *
 * Pixel + CAPI use the same id so Meta deduplicates the two channels.
 */
export function generateEventId(uid: string, eventName: string): string {
  return `${uid}:${eventName}`;
}

/** Reads the FB cookies the Pixel maintains. Both may be absent. */
export function getPixelCookies(): { fbp?: string; fbc?: string } {
  if (typeof document === 'undefined') return {};
  const out: { fbp?: string; fbc?: string } = {};
  for (const part of document.cookie.split(';')) {
    const trimmed = part.trim();
    if (trimmed.startsWith('_fbp=')) out.fbp = decodeURIComponent(trimmed.slice(5));
    else if (trimmed.startsWith('_fbc=')) out.fbc = decodeURIComponent(trimmed.slice(5));
  }
  return out;
}

/**
 * If the current URL carries an `fbclid` param and no `_fbc` cookie exists
 * yet, write `_fbc` in Meta's expected format scoped to `.gobo.show` so the
 * value is visible to both www.gobo.show and app.gobo.show.
 *
 * Format: fb.1.{timestamp_ms}.{fbclid}  (Meta's documented shape)
 *
 * Note: setting a parent-domain cookie from localhost will be rejected by the
 * browser — that's fine in dev, but means cross-subdomain attribution can't
 * be exercised locally. Verify in staging / preview deploys at *.gobo.show.
 */
export function captureFbclidIfMissing(): void {
  if (typeof window === 'undefined') return;
  try {
    const params = new URLSearchParams(window.location.search);
    const fbclid = params.get('fbclid');
    if (!fbclid) return;
    if (getPixelCookies().fbc) return;

    const value = `fb.1.${Date.now()}.${fbclid}`;
    const ninetyDays = 60 * 60 * 24 * 90;
    // domain=.gobo.show works on both www.gobo.show and app.gobo.show; it is
    // silently dropped by browsers on other hostnames (e.g. localhost), which
    // is the right local-dev behavior.
    document.cookie = `_fbc=${encodeURIComponent(value)}; domain=.gobo.show; path=/; max-age=${ninetyDays}; secure; samesite=lax`;
  } catch (err) {
    Bugsnag.notify(err instanceof Error ? err : new Error(String(err)), (event) => {
      event.addMetadata('meta_pixel', { phase: 'captureFbclid' });
    });
  }
}
